import moment from 'moment';

export const DATE_RANGE_PRESET_KEYS = Object.freeze({
    LAST_7_DAYS: 'last-7-days',
    LAST_30_DAYS: 'last-30-days',
    LAST_90_DAYS: 'last-90-days',
    PREV_WEEK: 'prev-week',
    PREV_MONTH: 'prev-month',
    PREV_QUARTER: 'prev-quarter',
    THIS_WEEK: 'this-week',
    THIS_MONTH: 'this-month',
    THIS_QUARTER: 'this-quarter',
    THIS_YEAR: 'this-year',
    TODAY: 'today',
});

/**
 * @returns {Array<{ key: string, label: string, resolve: () => [moment.Moment, moment.Moment], slot: string }>}
 */
export function getDateRangePresetDefinitions() {
    const now = moment();
    const previousWeek = now.clone().subtract(1, 'week');
    const previousMonth = now.clone().subtract(1, 'month');
    const previousQuarter = now.clone().subtract(1, 'quarter');

    return [
        {
            key: DATE_RANGE_PRESET_KEYS.TODAY,
            label: 'Today',
            resolve: () => [now.clone(), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
            label: 'Last 7 Days',
            resolve: () => [now.clone().subtract(6, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
            label: 'Last 30 Days',
            resolve: () => [now.clone().subtract(29, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
            label: 'Last 90 Days',
            resolve: () => [now.clone().subtract(89, 'days'), now.clone()],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_WEEK,
            label: 'Prev. Week',
            resolve: () => [
                previousWeek.clone().startOf('isoWeek'),
                previousWeek.clone().endOf('isoWeek'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_MONTH,
            label: 'Prev. Month',
            resolve: () => [
                previousMonth.clone().startOf('month'),
                previousMonth.clone().endOf('month'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
            label: 'Prev. Quarter',
            resolve: () => [
                previousQuarter.clone().startOf('quarter'),
                previousQuarter.clone().endOf('quarter'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_WEEK,
            label: 'This Week',
            resolve: () => [
                now.clone().startOf('isoWeek'),
                now.clone().endOf('isoWeek'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_MONTH,
            label: 'This Month',
            resolve: () => [
                now.clone().startOf('month'),
                now.clone().endOf('month'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
            label: 'This Quarter',
            resolve: () => [
                now.clone().startOf('quarter'),
                now.clone().endOf('quarter'),
            ],
            slot: 'preset-date-range-button',
        },
        {
            key: DATE_RANGE_PRESET_KEYS.THIS_YEAR,
            label: 'This Year',
            resolve: () => [
                now.clone().startOf('year'),
                now.clone().endOf('year'),
            ],
            slot: 'preset-date-range-button',
        },
    ];
}

/**
 * @param {string} key
 * @returns {{ key: string, label: string, resolve: Function }|undefined}
 */
export function getDateRangePresetDefinitionByKey(key) {
    if (!key) return undefined;
    return getDateRangePresetDefinitions().find(
        (preset) => preset.key === String(key)
    );
}

/**
 * @param {string} key
 * @returns {[string, string]|null}
 */
export function resolveDateRangeFromPresetKey(key) {
    const definition = getDateRangePresetDefinitionByKey(key);
    if (!definition) return null;

    const [start, end] = definition.resolve();
    return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string|null}
 */
export function getPresetKeyForRange(startDate, endDate) {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (!startMoment.isValid() || !endMoment.isValid()) {
        return null;
    }

    const targetStart = startMoment.clone().startOf('day');
    const targetEnd = endMoment.clone().startOf('day');

    const matched = getDateRangePresetDefinitions().find((preset) => {
        const [presetStart, presetEnd] = preset.resolve();
        return (
            presetStart.clone().startOf('day').isSame(targetStart, 'day') &&
            presetEnd.clone().startOf('day').isSame(targetEnd, 'day')
        );
    });

    return matched?.key ?? null;
}

/**
 * @param {string} key
 * @returns {string}
 */
export function getPresetLabelByKey(key) {
    return getDateRangePresetDefinitionByKey(key)?.label ?? '';
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string}
 */
export function getPresetLabelForRange(startDate, endDate) {
    const key = getPresetKeyForRange(startDate, endDate);
    return key ? getPresetLabelByKey(key) : '';
}

/**
 * Title-friendly label for a date range (e.g. "August", "Q2", "2026" for calendar presets).
 * @param {string} startDate
 * @param {string} endDate
 * @param {string|null} [presetKey]
 * @returns {string}
 */
export function getDateRangeTitleLabel(startDate, endDate, presetKey = null) {
    const key =
        presetKey && getDateRangePresetDefinitionByKey(presetKey)
            ? String(presetKey)
            : getPresetKeyForRange(startDate, endDate);
    const presetLabel = key ? getPresetLabelByKey(key) : '';

    if (!presetLabel) {
        return '';
    }

    const rangeMoment = moment(startDate);
    if (!rangeMoment.isValid()) {
        return presetLabel;
    }

    switch (key) {
        case DATE_RANGE_PRESET_KEYS.TODAY:
            return '';
        case DATE_RANGE_PRESET_KEYS.THIS_MONTH:
        case DATE_RANGE_PRESET_KEYS.PREV_MONTH:
            return rangeMoment.format('MMMM');
        case DATE_RANGE_PRESET_KEYS.THIS_QUARTER:
        case DATE_RANGE_PRESET_KEYS.PREV_QUARTER:
            return `Q${rangeMoment.quarter()}`;
        case DATE_RANGE_PRESET_KEYS.THIS_YEAR:
            return rangeMoment.format('YYYY');
        default:
            return presetLabel;
    }
}

/** Preset keys shown per filter surface (picker UI only). */
export const DATE_RANGE_PRESET_KEYS_BY_CONTEXT = Object.freeze({
    'template-stats': [
        DATE_RANGE_PRESET_KEYS.TODAY,
        DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_WEEK,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
    ],
    performance: [
        DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
        DATE_RANGE_PRESET_KEYS.THIS_YEAR,
    ],
    default: [
        DATE_RANGE_PRESET_KEYS.TODAY,
        DATE_RANGE_PRESET_KEYS.LAST_7_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_30_DAYS,
        DATE_RANGE_PRESET_KEYS.LAST_90_DAYS,
        DATE_RANGE_PRESET_KEYS.THIS_MONTH,
        DATE_RANGE_PRESET_KEYS.PREV_MONTH,
        DATE_RANGE_PRESET_KEYS.THIS_QUARTER,
        DATE_RANGE_PRESET_KEYS.PREV_QUARTER,
        DATE_RANGE_PRESET_KEYS.THIS_YEAR,
    ],
});

function mapPresetDefinitionToPickerOption(preset) {
    return {
        key: preset.key,
        label: preset.label,
        value: preset.resolve().map((date) => date.toDate()),
        slot: preset.slot,
    };
}

/**
 * Date-picker preset buttons for a given surface.
 * @param {'template-stats'|'performance'|'default'} [context='default']
 * @returns {Array<{ key: string, label: string, value: Date[], slot: string }>}
 */
export function getDateRangePresetsForContext(context = 'default') {
    const allowedKeys =
        DATE_RANGE_PRESET_KEYS_BY_CONTEXT[context] ??
        DATE_RANGE_PRESET_KEYS_BY_CONTEXT.default;
    const definitionsByKey = new Map(
        getDateRangePresetDefinitions().map((preset) => [preset.key, preset])
    );

    return allowedKeys
        .map((key) => definitionsByKey.get(key))
        .filter(Boolean)
        .map(mapPresetDefinitionToPickerOption);
}

/** @deprecated Prefer getDateRangePresetsForContext('default') */
export function getDefaultDateRangePresets() {
    return getDateRangePresetsForContext('default');
}

/**
 * @param {{ siteIds?: string[], areaIds?: string[], auditorIds?: string[], dateRange?: string[], dateRangePreset?: string|null }} filters
 * @returns {{ siteIds: string[], areaIds: string[], auditorIds: string[], dateRange?: string[], dateRangePreset?: string }}
 */
export function serializeDateFiltersForStorage(filters = {}) {
    const siteIds = Array.isArray(filters.siteIds) ? filters.siteIds : [];
    const areaIds = Array.isArray(filters.areaIds) ? filters.areaIds : [];
    const auditorIds = Array.isArray(filters.auditorIds)
        ? filters.auditorIds
        : [];

    const presetKey =
        filters.dateRangePreset &&
        getDateRangePresetDefinitionByKey(filters.dateRangePreset)
            ? String(filters.dateRangePreset)
            : inferDateRangePresetKey(filters.dateRange);

    if (presetKey) {
        return {
            siteIds,
            areaIds,
            auditorIds,
            dateRangePreset: presetKey,
        };
    }

    const dateRange = normalizeDateRangeStrings(filters.dateRange);
    return {
        siteIds,
        areaIds,
        auditorIds,
        ...(dateRange.length >= 2 ? { dateRange } : {}),
    };
}

/**
 * @param {object} stored
 * @param {[string, string]} fallbackRange
 * @returns {{ siteIds: string[], areaIds: string[], auditorIds: string[], dateRange: string[], dateRangePreset: string|null }}
 */
export function deserializeDateFiltersFromStorage(
    stored = {},
    fallbackRange = []
) {
    const siteIds = Array.isArray(stored.siteIds) ? stored.siteIds : [];
    const areaIds = Array.isArray(stored.areaIds) ? stored.areaIds : [];
    const auditorIds = Array.isArray(stored.auditorIds)
        ? stored.auditorIds
        : [];

    const fallback = normalizeDateRangeStrings(fallbackRange);
    const safeFallback = fallback.length >= 2 ? fallback : [];

    if (stored.dateRangePreset) {
        const resolved = resolveDateRangeFromPresetKey(stored.dateRangePreset);
        if (resolved) {
            return {
                siteIds,
                areaIds,
                auditorIds,
                dateRange: resolved,
                dateRangePreset: String(stored.dateRangePreset),
            };
        }
    }

    const dateRange = normalizeDateRangeStrings(stored.dateRange);
    const resolvedRange = dateRange.length >= 2 ? dateRange : safeFallback;

    return {
        siteIds,
        areaIds,
        auditorIds,
        dateRange: resolvedRange,
        dateRangePreset: null,
    };
}

/**
 * @param {string[]|null|undefined} dateRange
 * @returns {string|null}
 */
export function inferDateRangePresetKey(dateRange) {
    const normalized = normalizeDateRangeStrings(dateRange);
    if (normalized.length < 2) {
        return null;
    }

    return getPresetKeyForRange(normalized[0], normalized[1]);
}

/**
 * @param {string[]|null|undefined} values
 * @returns {string[]}
 */
export function normalizeDateRangeStrings(values) {
    if (!Array.isArray(values) || values.length < 2) {
        return [];
    }

    const [start, end] = values;
    const normalizedStart = String(start);
    const normalizedEnd = String(end);
    const startValid = moment(normalizedStart, 'YYYY-MM-DD', true).isValid();
    const endValid = moment(normalizedEnd, 'YYYY-MM-DD', true).isValid();

    if (!startValid || !endValid) {
        return [];
    }

    return [normalizedStart, normalizedEnd];
}

export function formatMediumDate(isoString) {
    if (!isoString) return '';
    return moment.utc(isoString).format('MMM D, YYYY');
}

/**
 * Convert a date-only value into a UTC day boundary ISO string.
 * @param {string|Date} dateValue
 * @param {'start'|'end'} boundary
 * @returns {string|null}
 */
function toUtcDayBoundaryIso(dateValue, boundary) {
    if (!dateValue) return null;

    const localMoment = moment(dateValue);
    if (!localMoment.isValid()) return null;

    const dateString = localMoment.format('YYYY-MM-DD');
    const utcMoment = moment.utc(dateString, 'YYYY-MM-DD', true);
    if (!utcMoment.isValid()) return null;

    return boundary === 'end'
        ? utcMoment.endOf('day').toISOString()
        : utcMoment.startOf('day').toISOString();
}

/**
 * Normalize a date range input into a start/end pair.
 * @param {string|Date|Array<string|Date>} value
 * @returns {{start: string|Date, end: string|Date}|null}
 */
export function normalizeDateRangeInput(value) {
    if (!value) return null;

    if (Array.isArray(value)) {
        const [start, end] = value;
        const rangeStart = start || end;
        const rangeEnd = end || start;
        return rangeStart ? { start: rangeStart, end: rangeEnd } : null;
    }

    return { start: value, end: value };
}

/**
 * Convert a date-only value into a UTC start-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayStartIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'start');
}

/**
 * Convert a date-only value into a UTC end-of-day ISO string.
 * @param {string|Date} dateValue
 * @returns {string|null}
 */
export function toUtcDayEndIso(dateValue) {
    return toUtcDayBoundaryIso(dateValue, 'end');
}
